import { FormReviewData, DestinationHealthInfo } from "@/types";
import { HealthRecommendations } from "@/components/common";

interface DestinationHealthInfoSectionProps {
  formData: FormReviewData;
  destinationInfoMap: Record<string, DestinationHealthInfo>;
  loadingDestinations: boolean;
  expandedDestinations: Record<number, boolean>;
  toggleDestination: (index: number) => void;
}

export function DestinationHealthInfoSection({
  formData,
  destinationInfoMap,
  loadingDestinations,
  expandedDestinations,
  toggleDestination,
}: DestinationHealthInfoSectionProps) {
  const trip = formData?.tripDetails;
  if (!trip?.destinations || trip.destinations.length === 0) return null;

  const hasAnyInfo = trip.destinations.some(
    (dest) => destinationInfoMap[dest.destination_country]
  );

  if (!hasAnyInfo && !loadingDestinations) return null;

  return (
    <div className="info-card destination-health-info">
      <div
        style={{
          background: "linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)",
          padding: "20px",
          borderRadius: "12px 12px 0 0",
          marginBottom: "20px",
        }}
      >
        <h3
          style={{
            margin: 0,
            color: "white",
            fontSize: "24px",
            fontWeight: "700",
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          Destination Health Recommendations
        </h3>
        <p
          style={{
            margin: "8px 0 0 40px",
            color: "rgba(255, 255, 255, 0.9)",
            fontSize: "14px",
          }}
        >
          Review health requirements and recommendations for each destination
        </p>
      </div>

      {loadingDestinations ? (
        <div
          style={{
            padding: "60px 20px",
            textAlign: "center",
            backgroundColor: "#f9fafb",
            borderRadius: "12px",
          }}
        >
          <div
            style={{
              display: "inline-block",
              width: "48px",
              height: "48px",
              border: "4px solid #e5e7eb",
              borderTop: "4px solid #14b8a6",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          ></div>
          <p
            style={{
              marginTop: "16px",
              color: "#6b7280",
              fontSize: "16px",
              fontWeight: "500",
            }}
          >
            Loading health recommendations...
          </p>
        </div>
      ) : (
        <div
          className="destinations-health-list"
          style={{ display: "flex", flexDirection: "column", gap: "24px" }}
        >
          {trip.destinations
            .sort((a, b) => a.order - b.order)
            .map((dest, index) => {
              const info = destinationInfoMap[dest.destination_country];
              if (!info) return null;

              const isExpanded = expandedDestinations[index];
              const requiredVaccines =
                info.health_recommendations?.vaccine_details?.required || [];
              const recommendedVaccines =
                info.health_recommendations?.vaccine_details?.recommended || [];
              const conditionalVaccines =
                info.health_recommendations?.vaccine_details?.conditional || [];
              const medications =
                info.health_recommendations?.medications || [];
              const malariaRisk = info.health_recommendations?.malaria_risk;
              const emergencyNumbers = info.emergency_numbers;

              const totalRecommendations =
                requiredVaccines.length +
                recommendedVaccines.length +
                conditionalVaccines.length +
                medications.length +
                (malariaRisk?.prophylaxis_recommended ? 1 : 0);

              const criticalCount = requiredVaccines.filter(
                (v) => v.risk_level === "critical"
              ).length;
              const conditionalEntryCount = conditionalVaccines.filter(
                (v) => v.risk_level === "conditional_entry"
              ).length;

              return (
                <div
                  key={index}
                  className="destination-health-card"
                  style={{
                    marginBottom: "0",
                    border: "2px solid #e5e7eb",
                    borderRadius: "16px",
                    overflow: "hidden",
                    boxShadow: isExpanded
                      ? "0 10px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)"
                      : "0 1px 3px 0 rgb(0 0 0 / 0.1)",
                    transition: "all 0.3s ease",
                  }}
                >
                  {/* Enhanced Header */}
                  <div
                    onClick={() => toggleDestination(index)}
                    style={{
                      padding: "20px 24px",
                      background: isExpanded
                        ? "linear-gradient(135deg, #f0f9ff 0%, #e0e7ff 100%)"
                        : "#ffffff",
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      borderBottom: isExpanded ? "2px solid #e5e7eb" : "none",
                      transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (!isExpanded) {
                        e.currentTarget.style.backgroundColor = "#f9fafb";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isExpanded) {
                        e.currentTarget.style.backgroundColor = "#ffffff";
                      }
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "16px",
                        flex: 1,
                      }}
                    >
                      <div
                        style={{
                          width: "48px",
                          height: "48px",
                          borderRadius: "12px",
                          background:
                            "linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "24px",
                          flexShrink: 0,
                        }}
                      >
                        {isExpanded ? "📍" : "✈️"}
                      </div>
                      <div style={{ flex: 1 }}>
                        <h4
                          style={{
                            margin: 0,
                            fontSize: "20px",
                            fontWeight: "700",
                            color: "#111827",
                            marginBottom: "4px",
                          }}
                        >
                          {info.destination?.name || dest.destination_country}
                        </h4>
                        {info.destination?.official_name && (
                          <p
                            style={{
                              margin: 0,
                              fontSize: "13px",
                              color: "#6b7280",
                              fontWeight: "500",
                            }}
                          >
                            {info.destination.official_name}
                          </p>
                        )}
                      </div>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        gap: "12px",
                        alignItems: "center",
                        flexWrap: "wrap",
                        justifyContent: "flex-end",
                      }}
                    >
                      {criticalCount > 0 && (
                        <span
                          style={{
                            padding: "6px 12px",
                            borderRadius: "8px",
                            fontSize: "12px",
                            fontWeight: "700",
                            backgroundColor: "#fef2f2",
                            color: "#dc2626",
                            border: "1px solid #fecaca",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <span>⚠️</span>
                          {criticalCount} Required
                        </span>
                      )}
                      {conditionalEntryCount > 0 && (
                        <span
                          style={{
                            padding: "6px 12px",
                            borderRadius: "8px",
                            fontSize: "12px",
                            fontWeight: "700",
                            backgroundColor: "#fffbeb",
                            color: "#f59e0b",
                            border: "1px solid #fde68a",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <span>🌐</span>
                          {conditionalEntryCount} Route-Based
                        </span>
                      )}
                      {totalRecommendations > 0 && (
                        <span
                          style={{
                            padding: "6px 12px",
                            borderRadius: "8px",
                            fontSize: "12px",
                            fontWeight: "700",
                            backgroundColor: "#eff6ff",
                            color: "#2563eb",
                            border: "1px solid #bfdbfe",
                          }}
                        >
                          {totalRecommendations} Total
                        </span>
                      )}
                      <div
                        style={{
                          width: "32px",
                          height: "32px",
                          borderRadius: "8px",
                          backgroundColor: isExpanded ? "#14b8a6" : "#e5e7eb",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "all 0.3s ease",
                        }}
                      >
                        <span
                          style={{
                            fontSize: "16px",
                            transform: isExpanded
                              ? "rotate(180deg)"
                              : "rotate(0deg)",
                            transition: "transform 0.3s ease",
                            display: "inline-block",
                            color: isExpanded ? "white" : "#374151",
                          }}
                        >
                          ▼
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Expandable Content with Enhanced Component */}
                  {isExpanded && (
                    <div
                      className="health-recommendations-content"
                      style={{
                        padding: "24px",
                        backgroundColor: "#f9fafb",
                      }}
                    >
                      {/* Enhanced Health Recommendations Component */}
                      <HealthRecommendations destination={info} />

                      {/* No Requirements Message */}
                      {!requiredVaccines.length &&
                        !recommendedVaccines.length &&
                        !conditionalVaccines.length &&
                        !malariaRisk?.prophylaxis_recommended &&
                        !medications.length && (
                          <div
                            style={{
                              padding: "32px 20px",
                              textAlign: "center",
                              backgroundColor: "#f0fdf4",
                              borderRadius: "12px",
                              border: "2px solid #86efac",
                            }}
                          >
                            <span style={{ fontSize: "48px" }}>✅</span>
                            <p
                              style={{
                                margin: "12px 0 0 0",
                                color: "#065f46",
                                fontWeight: "700",
                                fontSize: "16px",
                              }}
                            >
                              No special health requirements for this
                              destination
                            </p>
                            <p
                              style={{
                                margin: "4px 0 0 0",
                                color: "#059669",
                                fontSize: "14px",
                              }}
                            >
                              Standard routine vaccinations recommended
                            </p>
                          </div>
                        )}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
}

// Sub-components for better organization
function VaccineSection({
  title,
  icon,
  vaccines,
  bgColor,
  borderColor,
  textColor,
}: {
  title: string;
  icon: string;
  vaccines: any[];
  bgColor: string;
  borderColor: string;
  textColor: string;
}) {
  return (
    <div className="health-section" style={{ marginBottom: "16px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "8px",
        }}
      >
        <span style={{ fontSize: "20px" }}>{icon}</span>
        <h5
          style={{
            margin: 0,
            fontSize: "16px",
            fontWeight: "bold",
            color: borderColor,
          }}
        >
          {title}
        </h5>
      </div>
      <div style={{ paddingLeft: "28px" }}>
        {vaccines.map((vaccine, idx) => (
          <div
            key={idx}
            style={{
              marginBottom: "12px",
              padding: "12px",
              backgroundColor: bgColor,
              borderRadius: "8px",
              borderLeft: `4px solid ${borderColor}`,
            }}
          >
            <div
              style={{
                fontWeight: "bold",
                color: textColor,
                marginBottom: "4px",
              }}
            >
              {vaccine.vaccine}
            </div>
            <div
              style={{
                fontSize: "14px",
                color: textColor,
              }}
            >
              Protection against: {vaccine.disease}
            </div>
            {vaccine.summary && (
              <div
                style={{
                  fontSize: "13px",
                  color: "#666",
                  marginTop: "6px",
                }}
              >
                {vaccine.summary}
              </div>
            )}

            {/* Customer Summary - Key Recommendation */}
            {vaccine.conditions?.customer_summary && (
              <div
                style={{
                  marginTop: "8px",
                  padding: "8px",
                  backgroundColor: "#d1fae5",
                  border: "1px solid #10b981",
                  borderRadius: "6px",
                }}
              >
                <div
                  style={{
                    fontSize: "12px",
                    fontWeight: "bold",
                    color: "#065f46",
                    marginBottom: "4px",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <span>ℹ️</span>
                  Recommendation Summary:
                </div>
                <div
                  style={{
                    fontSize: "13px",
                    color: "#047857",
                    fontWeight: "600",
                  }}
                >
                  {vaccine.conditions.customer_summary}
                </div>
              </div>
            )}

            {/* Detailed Requirements & Conditions */}
            {vaccine.conditions?.full_text && (
              <div
                style={{
                  marginTop: "8px",
                  padding: "8px",
                  backgroundColor: "#dbeafe",
                  border: "1px solid #3b82f6",
                  borderRadius: "6px",
                }}
              >
                <div
                  style={{
                    fontSize: "12px",
                    fontWeight: "bold",
                    color: "#1e40af",
                    marginBottom: "4px",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <span>📋</span>
                  Detailed Requirements & Conditions:
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#1e3a8a",
                    whiteSpace: "pre-line",
                    lineHeight: "1.5",
                  }}
                >
                  {vaccine.conditions.full_text}
                </div>
              </div>
            )}

            {/* Display outbreak/area information */}
            {vaccine.conditions?.areas && (
              <div
                style={{
                  marginTop: "8px",
                  padding: "8px",
                  backgroundColor: "#fef3c7",
                  border: "1px solid #f59e0b",
                  borderRadius: "6px",
                }}
              >
                <div
                  style={{
                    fontSize: "12px",
                    fontWeight: "bold",
                    color: "#92400e",
                    marginBottom: "4px",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <span>📍</span>
                  Affected Areas / Outbreak Information:
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#78350f",
                  }}
                >
                  {typeof vaccine.conditions.areas === "string"
                    ? vaccine.conditions.areas
                    : JSON.stringify(vaccine.conditions.areas)}
                </div>
              </div>
            )}

            {/* Special Population Guidance */}
            {vaccine.conditions?.parsed_data && (
              <div
                style={{
                  marginTop: "8px",
                  padding: "8px",
                  backgroundColor: "#fae8ff",
                  border: "1px solid #a855f7",
                  borderRadius: "6px",
                }}
              >
                <div
                  style={{
                    fontSize: "12px",
                    fontWeight: "bold",
                    color: "#6b21a8",
                    marginBottom: "6px",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <span>👥</span>
                  Special Population Guidance:
                </div>
                <div style={{ fontSize: "12px", color: "#581c87" }}>
                  {vaccine.conditions.parsed_data.infant_guidance && (
                    <div style={{ marginBottom: "4px" }}>
                      <strong>• Infants:</strong>{" "}
                      {vaccine.conditions.parsed_data.infant_guidance}
                    </div>
                  )}
                  {vaccine.conditions.parsed_data.special_populations && (
                    <div>
                      <strong style={{ display: "block", marginBottom: "4px" }}>
                        Special Populations:
                      </strong>
                      {Object.entries(
                        vaccine.conditions.parsed_data.special_populations
                      ).map(([key, value]: [string, any]) => (
                        <div
                          key={key}
                          style={{ marginLeft: "12px", marginBottom: "2px" }}
                        >
                          ▸{" "}
                          <strong style={{ textTransform: "capitalize" }}>
                            {key.replace(/_/g, " ")}:
                          </strong>{" "}
                          {value}
                        </div>
                      ))}
                    </div>
                  )}
                  {vaccine.conditions.parsed_data.age_restrictions && (
                    <div style={{ marginTop: "4px" }}>
                      <strong>• Age Restrictions:</strong>{" "}
                      {vaccine.conditions.parsed_data.age_restrictions}
                    </div>
                  )}
                </div>
              </div>
            )}
            {vaccine.clinical_guidance && (
              <div
                style={{
                  fontSize: "12px",
                  color: "#4b5563",
                  marginTop: "6px",
                  fontStyle: "italic",
                }}
              >
                Clinical note: {vaccine.clinical_guidance}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function MalariaRiskSection({ malariaRisk }: { malariaRisk: any }) {
  return (
    <div className="health-section" style={{ marginBottom: "16px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "8px",
        }}
      >
        <span style={{ fontSize: "20px" }}>🦟</span>
        <h5
          style={{
            margin: 0,
            fontSize: "16px",
            fontWeight: "bold",
            color: "#ea580c",
          }}
        >
          Malaria Risk
        </h5>
      </div>
      <div
        style={{
          paddingLeft: "28px",
          padding: "12px",
          backgroundColor: "#fff7ed",
          borderRadius: "8px",
          borderLeft: "4px solid #ea580c",
        }}
      >
        <div
          style={{
            fontWeight: "bold",
            color: "#9a3412",
            marginBottom: "6px",
          }}
        >
          Risk Level: {malariaRisk.risk_level}
        </div>
        {malariaRisk.customer_summary && (
          <div
            style={{
              fontSize: "14px",
              color: "#7c2d12",
              marginBottom: "8px",
            }}
          >
            {malariaRisk.customer_summary}
          </div>
        )}
        {/* Display malaria risk areas */}
        {malariaRisk.risk_areas && (
          <div
            style={{
              marginTop: "8px",
              marginBottom: "8px",
              padding: "8px",
              backgroundColor: "#fef3c7",
              border: "1px solid #f59e0b",
              borderRadius: "6px",
            }}
          >
            <div
              style={{
                fontSize: "12px",
                fontWeight: "bold",
                color: "#92400e",
                marginBottom: "4px",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <span>📍</span>
              Malaria Risk Areas:
            </div>
            <div
              style={{
                fontSize: "12px",
                color: "#78350f",
              }}
            >
              {typeof malariaRisk.risk_areas === "string"
                ? malariaRisk.risk_areas
                : JSON.stringify(malariaRisk.risk_areas)}
            </div>
          </div>
        )}
        {malariaRisk.prophylaxis_drugs &&
          malariaRisk.prophylaxis_drugs.length > 0 && (
            <div style={{ fontSize: "14px", color: "#666" }}>
              <strong>Recommended prophylaxis:</strong>{" "}
              {malariaRisk.prophylaxis_drugs.join(", ")}
            </div>
          )}
      </div>
    </div>
  );
}

function MedicationsSection({ medications }: { medications: any[] }) {
  return (
    <div className="health-section" style={{ marginBottom: "16px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "8px",
        }}
      >
        <span style={{ fontSize: "20px" }}>💊</span>
        <h5
          style={{
            margin: 0,
            fontSize: "16px",
            fontWeight: "bold",
            color: "#059669",
          }}
        >
          Other Medications
        </h5>
      </div>
      <div style={{ paddingLeft: "28px" }}>
        {medications.map((med, idx) => (
          <div
            key={idx}
            style={{
              marginBottom: "12px",
              padding: "12px",
              backgroundColor: "#f0fdf4",
              borderRadius: "8px",
              borderLeft: "4px solid #059669",
            }}
          >
            <div
              style={{
                fontWeight: "bold",
                color: "#065f46",
                marginBottom: "4px",
              }}
            >
              {med.disease}
            </div>
            <div
              style={{
                fontSize: "14px",
                color: "#064e3b",
              }}
            >
              Type: {med.medication_type}
            </div>
            {med.risk_level && (
              <div
                style={{
                  fontSize: "13px",
                  color: "#666",
                  marginTop: "6px",
                }}
              >
                Risk Level: {med.risk_level}
              </div>
            )}
            {/* Display medication risk areas */}
            {med.risk_areas && (
              <div
                style={{
                  marginTop: "8px",
                  padding: "8px",
                  backgroundColor: "#fef3c7",
                  border: "1px solid #f59e0b",
                  borderRadius: "6px",
                }}
              >
                <div
                  style={{
                    fontSize: "12px",
                    fontWeight: "bold",
                    color: "#92400e",
                    marginBottom: "4px",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <span>📍</span>
                  Risk Areas:
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#78350f",
                  }}
                >
                  {typeof med.risk_areas === "string"
                    ? med.risk_areas
                    : JSON.stringify(med.risk_areas)}
                </div>
              </div>
            )}
            {med.special_conditions && (
              <div
                style={{
                  fontSize: "12px",
                  color: "#4b5563",
                  marginTop: "6px",
                  fontStyle: "italic",
                }}
              >
                Special conditions: {med.special_conditions}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
